// externals
import { useRef } from 'react';
import { useSelector } from 'react-redux';

// libraries
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';

// store
import { changeVehicleSelectors } from '../../store';

// utils
import { useScrollIntoView } from '../../utils/useScrollIntoView';

// styles
import { ChangeVehicleHeaderContainer, ChangeVehicleHeaderContainerWrapper } from '../changeVehicle/ChangeVehicle.interstate.style';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

interface ChangeVehicleHeaderInterstateProps {
    isInsideSlideOut?: boolean;
}

export const ChangeVehicleHeaderInterstate = ({ isInsideSlideOut = false }: ChangeVehicleHeaderInterstateProps) => {
    const alertRef = useRef<HTMLDivElement>(null);

    // AI-related selectors
    const isDealerAISearchEnabled = useSelector(dealerSelectors.isDealerAISearchEnabled);

    const showSearchError = useSelector(changeVehicleSelectors.getShowSearchError);
    const isSearchError = !isDealerAISearchEnabled && showSearchError;
    const isFiltersInitialized = useSelector(changeVehicleSelectors.getIsFiltersInitialized);
    const searchSummary = useSelector(changeVehicleSelectors.getSearchSummary);
    const showCatalogError = useSelector(changeVehicleSelectors.getShowCatalogError);
    const showProcessingError = useSelector(changeVehicleSelectors.getShowProcessingError);
    const showBuildVehicleError = useSelector(changeVehicleSelectors.getShowBuildVehicleError);
    const shouldScroll = showProcessingError || showBuildVehicleError;

    useScrollIntoView(alertRef, shouldScroll);

    return (
        <ChangeVehicleHeaderContainerWrapper>
            <ChangeVehicleHeaderContainer className="change-vehicle-header" data-testid="change-vehicle-header">
                {!isInsideSlideOut && (
                    <Typography
                        variant="h1"
                        className="change-vehicle-title"
                        data-testid="changeVehicle"
                        sx={{ marginBottom: '16px' }}
                    >
                        Change Vehicle
                    </Typography>
                )}

                <div ref={alertRef} className="change-vehicle-alert-container">
                    {showProcessingError && (
                        <Alert
                            id="change-vehicle-error"
                            type="error"
                            sx={{ marginBottom: '16px' }}
                            data-testid="change-vehicle-error"
                        >
                            <Typography variant="body-sm" tag="span">
                                We can't calculate the deal with the selected vehicle at the moment. Try again or choose another
                                vehicle.
                            </Typography>
                        </Alert>
                    )}
                    {showBuildVehicleError && (
                        <Alert type="error" sx={{ marginBottom: '16px' }} data-testid="build-vehicle-error">
                            <Typography variant="body-sm" tag="span">
                                Vehicle build request unsuccessful. Please retry or contact support if the issue persists.
                            </Typography>
                        </Alert>
                    )}

                    {isSearchError && !searchSummary && !isFiltersInitialized && (
                        <Alert type="error" sx={{ marginBottom: '16px' }} data-testid="initial-loading-filters-error">
                            <Typography variant="body-sm" tag="span">
                                Something went wrong while processing your request. Please try again.
                            </Typography>
                        </Alert>
                    )}

                    {showCatalogError && (
                        <Alert type="error" sx={{ marginBottom: '16px' }} data-testid="show-catalog-filters-error">
                            <Typography variant="body-sm" tag="span">
                                Failed to load data from the Vehicle Catalog.
                            </Typography>
                        </Alert>
                    )}
                </div>
            </ChangeVehicleHeaderContainer>
        </ChangeVehicleHeaderContainerWrapper>
    );
};
